import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMoreVertical, FiTrendingUp, FiArrowUp, FiArrowDown } = FiIcons;

const ActivityAnalysis = ({ activityRate = 0, analysisData = [] }) => {
  // Ensure percentage is between 0-100
  const percentage = Math.min(100, Math.max(0, activityRate));
  
  // Calculate average fit score if data is available
  const averageFitScore = analysisData.length > 0 
    ? Math.round(analysisData.reduce((sum, item) => sum + (item.fitScore || 0), 0) / analysisData.length * 10) / 10
    : 0;

  // Calculate success rate (websites with fit score >= 7)
  const highPotentialCount = analysisData.filter(item => (item.fitScore || 0) >= 7).length;
  const mediumPotentialCount = analysisData.filter(item => (item.fitScore || 0) >= 5 && (item.fitScore || 0) < 7).length;
  const lowPotentialCount = analysisData.filter(item => (item.fitScore || 0) < 5).length;
  const successRate = analysisData.length > 0 
    ? Math.round((highPotentialCount / analysisData.length) * 100)
    : 0;
  
  // Calculate week-over-week trend (if we have data with dates)
  const getRecentTrend = () => {
    if (analysisData.length < 2) return { trend: 0, positive: false };
    
    // Get sites analyzed in the last 7 days
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
    
    const thisWeekSites = analysisData.filter(item => {
      const date = new Date(item.analyzedAt || Date.now());
      return date >= oneWeekAgo && date <= now;
    });
    
    const lastWeekSites = analysisData.filter(item => {
      const date = new Date(item.analyzedAt || Date.now());
      return date >= twoWeeksAgo && date < oneWeekAgo;
    });
    
    const thisWeekAvg = thisWeekSites.length > 0 
      ? thisWeekSites.reduce((sum, item) => sum + (item.fitScore || 0), 0) / thisWeekSites.length
      : 0;
      
    const lastWeekAvg = lastWeekSites.length > 0 
      ? lastWeekSites.reduce((sum, item) => sum + (item.fitScore || 0), 0) / lastWeekSites.length
      : 0;
    
    if (lastWeekAvg === 0) return { trend: 0, positive: false };
    
    const trendPercent = Math.round(((thisWeekAvg - lastWeekAvg) / lastWeekAvg) * 100);
    return {
      trend: Math.abs(trendPercent),
      positive: trendPercent >= 0
    };
  };
  
  const trend = getRecentTrend();
  
  return (
    <div className="activity-analysis">
      <div className="analytics-header">
        <h3 className="analytics-title">Website Potential Analysis</h3>
        <SafeIcon icon={FiMoreVertical} style={{ cursor: 'pointer' }} />
      </div>
      
      <div className="chart-container">
        <div className="gauge-chart">
          <svg width="100%" height="100%" viewBox="0 0 200 100">
            <path 
              d="M20 80 A 60 60 0 0 1 180 80" 
              stroke="#f3f4f6" 
              strokeWidth="16" 
              fill="none"
            />
            <path 
              d="M20 80 A 60 60 0 0 1 180 80" 
              stroke="#ec4899" 
              strokeWidth="16" 
              strokeDasharray="251.2" 
              strokeDashoffset={251.2 - (251.2 * successRate / 100)}
              fill="none"
            />
            <circle cx="20" cy="80" r="6" fill="#ec4899" />
            <circle cx="180" cy="80" r="6" fill="#f3f4f6" />
            <text x="100" y="50" textAnchor="middle" fontSize="24" fontWeight="bold">{successRate}%</text>
            <foreignObject x="70" y="55" width="60" height="30">
              <div style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>High Potential</div>
            </foreignObject>
          </svg>
        </div>
      </div>
      
      <div className="analysis-metrics">
        <div className="metric-item">
          <div className="metric-value">{averageFitScore}</div>
          <div className="metric-label">Avg. Fit Score</div>
        </div>
        <div className="metric-item">
          <div className="metric-value">
            <SafeIcon icon={trend.positive ? FiArrowUp : FiArrowDown} 
                     style={{ 
                       color: trend.positive ? '#4ade80' : '#f87171',
                       opacity: trend.trend === 0 ? 0 : 1
                     }} />
            {trend.trend > 0 ? `${trend.trend}%` : '-'}
          </div>
          <div className="metric-label">Weekly Trend</div>
        </div>
      </div>
      
      <div className="website-potential-breakdown">
        <div className="potential-bar">
          <div className="potential-segment high" 
               style={{ 
                 width: `${analysisData.length > 0 ? (highPotentialCount / analysisData.length) * 100 : 0}%` 
               }}></div>
          <div className="potential-segment medium" 
               style={{ 
                 width: `${analysisData.length > 0 ? (mediumPotentialCount / analysisData.length) * 100 : 0}%` 
               }}></div>
          <div className="potential-segment low" 
               style={{ 
                 width: `${analysisData.length > 0 ? (lowPotentialCount / analysisData.length) * 100 : 0}%` 
               }}></div>
        </div>
      </div>
      
      <div className="legend">
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: '#4ade80' }}></div>
          <span>High ({highPotentialCount})</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: '#fb923c' }}></div>
          <span>Medium ({mediumPotentialCount})</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: '#f87171' }}></div>
          <span>Low ({lowPotentialCount})</span>
        </div>
      </div>
      
      <style jsx="true">{`
        .website-potential-breakdown {
          margin: 1rem 0;
        }
        .potential-bar {
          height: 8px;
          width: 100%;
          background: #f3f4f6;
          border-radius: 4px;
          overflow: hidden;
          display: flex;
        }
        .potential-segment {
          height: 100%;
        }
        .potential-segment.high {
          background-color: #4ade80;
        }
        .potential-segment.medium {
          background-color: #fb923c;
        }
        .potential-segment.low {
          background-color: #f87171;
        }
      `}</style>
    </div>
  );
};

export default ActivityAnalysis;